"use client";

import { Fragment, useLayoutEffect, useRef, useState } from "react";
import { Combobox, Portal, Transition } from "@headlessui/react";
import { Check, ChevronDown, Globe } from "lucide-react";

export const languages = [
  { id: "en", name: "English" },
  { id: "ar", name: "Arabic" },
  { id: "zh", name: "Chinese" },
  { id: "da", name: "Danish" },
  { id: "nl", name: "Dutch" },
  { id: "fi", name: "Finnish" },
  { id: "fr", name: "French" },
  { id: "de", name: "German" },
  { id: "he", name: "Hebrew" },
  { id: "hi", name: "Hindi" },
  { id: "id", name: "Indonesian" },
  { id: "it", name: "Italian" },
  { id: "ja", name: "Japanese" },
  { id: "ko", name: "Korean" },
  { id: "ms", name: "Malay" },
  { id: "no", name: "Norwegian" },
  { id: "pl", name: "Polish" },
  { id: "pt", name: "Portuguese" },
  { id: "ru", name: "Russian" },
  { id: "es", name: "Spanish" },
  { id: "sv", name: "Swedish" },
  { id: "th", name: "Thai" },
  { id: "tr", name: "Turkish" },
  { id: "vi", name: "Vietnamese" },
];

interface LanguageSelectorProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

export default function LanguageSelector({
  value,
  onChange,
  disabled = false,
}: LanguageSelectorProps) {
  const [query, setQuery] = useState("");
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [optionsStyle, setOptionsStyle] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const filteredLanguages =
    query === ""
      ? languages
      : languages.filter((language) =>
          language.name
            .toLowerCase()
            .replace(/\s+/g, "")
            .includes(query.toLowerCase().replace(/\s+/g, ""))
        );

  return (
    <div className="w-full">
      <Combobox value={value} onChange={onChange} disabled={disabled}>
        {({ open }) => {
          // Track the button's rect so the portal-rendered options can
          // position themselves with `position: fixed` and escape clipping
          // ancestors (modal panels, scroll containers, etc).
          // Hook calls are inside the render-prop component; safe in v1.
          // eslint-disable-next-line react-hooks/rules-of-hooks
          useLayoutEffect(() => {
            if (!open || !wrapperRef.current) return;
            const update = () => {
              if (!wrapperRef.current) return;
              const r = wrapperRef.current.getBoundingClientRect();
              setOptionsStyle({ top: r.bottom + 4, left: r.left, width: r.width });
            };
            update();
            window.addEventListener("scroll", update, true);
            window.addEventListener("resize", update);
            return () => {
              window.removeEventListener("scroll", update, true);
              window.removeEventListener("resize", update);
            };
          }, [open]);

          return (
          <div ref={wrapperRef} className="relative mt-1">
            <Combobox.Button
              as="div"
              className="relative w-full cursor-default rounded-lg bg-background text-left border border-input focus-within:ring-2 focus-within:ring-primary focus-within:border-primary sm:text-sm transition-colors hover:border-accent-hover overflow-hidden"
            >
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-muted-foreground z-10 pointer-events-none">
                <Globe className="h-5 w-5" aria-hidden="true" />
              </span>
              <Combobox.Input
                className="w-full border-none py-3 pl-10 pr-10 text-base leading-5 text-foreground focus:ring-0 bg-transparent"
                displayValue={(id: string) =>
                  languages.find((l) => l.id === id)?.name ?? ""
                }
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search language..."
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <ChevronDown
                  className="h-5 w-5 text-muted-foreground"
                  aria-hidden="true"
                />
              </div>
            </Combobox.Button>
            <Portal>
            <Transition
              as={Fragment}
              show={open}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
              afterLeave={() => setQuery("")}
            >
              <Combobox.Options
                style={
                  optionsStyle
                    ? {
                        position: "fixed",
                        top: optionsStyle.top,
                        left: optionsStyle.left,
                        width: optionsStyle.width,
                      }
                    : undefined
                }
                className="max-h-60 overflow-auto rounded-md bg-popover py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm z-[1000]"
              >
                {filteredLanguages.length === 0 && query !== "" ? (
                   <div className="relative cursor-default select-none py-2 px-4 text-muted-foreground">
                    No language found.
                  </div>
                ) : (
                  filteredLanguages.map((language) => (
                    <Combobox.Option
                      key={language.id}
                      className={({ active }) =>
                        `relative cursor-default select-none py-2 pl-10 pr-4 ${
                          active
                            ? "bg-accent text-accent-foreground"
                            : "text-foreground"
                        }`
                      }
                      value={language.id}
                    >
                      {({ selected }) => (
                        <>
                          <span
                            className={`block truncate ${
                              selected ? "font-medium" : "font-normal"
                            }`}
                          >
                            {language.name}
                          </span>
                          {selected ? (
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary">
                              <Check className="h-5 w-5" aria-hidden="true" />
                            </span>
                          ) : null}
                        </>
                      )}
                    </Combobox.Option>
                  ))
                )}
              </Combobox.Options>
            </Transition>
            </Portal>
          </div>
          );
        }}
      </Combobox>
    </div>
  );
}
